import React, { useState } from 'react'
import '../../styles/ExpandedFeedbackModule/StarRating.scss'

const STARS = [1, 2, 3, 4, 5] as const

type Props = {
  value: number | null
  onChange: (value: number) => void
}

/**
 * 1-5 star rating input. Each star is a real <button> so the control is
 * keyboard-operable. Hovering previews the rating: every star up to and
 * including the hovered one is highlighted; otherwise the fill follows `value`.
 */
export const StarRating = ({ value, onChange }: Props) => {
  const [hovered, setHovered] = useState<number | null>(null)
  const active = hovered ?? value

  return (
    <div
      className="expanded-feedback-module__stars"
      role="radiogroup"
      aria-label="Rate your experience from 1 to 5 stars"
      onMouseLeave={() => setHovered(null)}
    >
      {STARS.map((star) => {
        const highlighted = active !== null && star <= active
        // A committed selection (value) fills the star solid regardless of hover;
        // a hover preview only outlines stars beyond the current selection.
        const selected = value !== null && star <= value
        const className = [
          'expanded-feedback-module__star',
          highlighted && 'expanded-feedback-module__star--filled',
          selected && 'expanded-feedback-module__star--selected'
        ].filter(Boolean).join(' ')
        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} star${star === 1 ? '' : 's'}`}
            className={className}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.9l-5.8 3.05 1.11-6.46-4.7-4.58 6.49-.94L12 2.5z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )
      })}
    </div>
  )
}
